import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { PerProductEligibilityProto } from "@noom/noom-contracts/noom_contracts/billing/product_catalog/product_eligibility_service";
import type { EvaluationTriggerContextProto } from "@noom/noom-contracts/noom_contracts/billing/shared";

import { isIneligibleOnlyDueToLocation } from "src/utils/addons/eligibilityDetails";
import { evaluateProductEligibility } from "src/utils/api/productEligibility";
import { captureException } from "src/utils/error";
import { createAppAsyncThunk } from "../async-thunk";
import {
  isRequestSuccess,
  requestState,
  type RequestState,
} from "../requestState";
import getStore, { type CoreReduxState } from "../store";

export type ProductEligibilityState = Record<
  string,
  RequestState<PerProductEligibilityProto>
>;

const productEligibilitySlice = createSlice({
  name: "productEligibility",
  initialState: {} as ProductEligibilityState,
  reducers: {
    setProductEligibility(
      state,
      action: PayloadAction<{
        productName: string;
        value: RequestState<PerProductEligibilityProto>;
      }>
    ) {
      return {
        ...state,
        [action.payload.productName]: action.payload.value,
      };
    },
  },
});

export const { setProductEligibility } = productEligibilitySlice.actions;

export default productEligibilitySlice;

const inFlightByProduct: Record<string, Promise<void>> = {};

function normalizeProductNames(productNames: string | string[]) {
  const names = Array.isArray(productNames) ? productNames : [productNames];
  return [...new Set(names.filter(Boolean))];
}

export type LoadProductEligibilityArg = {
  productNames: string | string[];
  triggerContext?: EvaluationTriggerContextProto;
  retry?: boolean;
};

export const loadProductEligibility = createAppAsyncThunk<
  LoadProductEligibilityArg,
  void
>("productEligibility/loadProductEligibility", async (arg, thunkAPI) => {
  const { productNames, triggerContext, retry = false } = arg;
  const names = normalizeProductNames(productNames);
  if (names.length === 0) {
    return;
  }

  // Always fetch at the decision point so a prior success (including
  // INELIGIBLE) cannot go stale. Only dedupe in-flight requests.
  // Products with a prior success are revalidated silently (stale-while-revalidate):
  // the cached verdict keeps the CTA stable while the request is in flight, and
  // the new verdict lands once the response arrives.
  const namesNeedingFetch = names.filter((name) => !inFlightByProduct[name]);
  if (namesNeedingFetch.length > 0) {
    const batchPromise = (async () => {
      try {
        for (const productName of namesNeedingFetch) {
          if (
            !isRequestSuccess(
              getProductEligibility(productName, thunkAPI.getState())
            )
          ) {
            thunkAPI.dispatch(
              setProductEligibility({
                productName,
                value: requestState.loading<PerProductEligibilityProto>(),
              })
            );
          }
        }

        const response = await evaluateProductEligibility(namesNeedingFetch, {
          triggerContext,
          retry,
        });
        const byName = new Map(
          response.results.map((result) => [result.productName, result])
        );

        for (const productName of namesNeedingFetch) {
          const result = byName.get(productName);
          thunkAPI.dispatch(
            setProductEligibility({
              productName,
              value: result
                ? requestState.success(result)
                : requestState.failure<PerProductEligibilityProto>(),
            })
          );
        }
      } catch (error) {
        // The thunk resolves after marking failures (consumers read the
        // request state, not the thunk result), so report here — callers
        // awaiting the dispatch never see this error.
        captureException(error, "loadProductEligibility");
        for (const productName of namesNeedingFetch) {
          thunkAPI.dispatch(
            setProductEligibility({
              productName,
              value: requestState.failure<PerProductEligibilityProto>(),
            })
          );
        }
      } finally {
        for (const productName of namesNeedingFetch) {
          delete inFlightByProduct[productName];
        }
      }
    })();

    for (const productName of namesNeedingFetch) {
      inFlightByProduct[productName] = batchPromise;
    }
  }

  await Promise.all(names.map((name) => inFlightByProduct[name]));
});

export function getProductEligibility(
  productName: string,
  state: CoreReduxState = getStore().getState()
): RequestState<PerProductEligibilityProto> | undefined {
  return state.productEligibility?.[productName];
}

export function isProductEligible(
  productName: string,
  state: CoreReduxState = getStore().getState()
) {
  const entry = getProductEligibility(productName, state);
  if (!isRequestSuccess(entry)) {
    return false;
  }

  const eligibilityState = entry.data?.purchaseEligibility?.eligibilityState;
  return eligibilityState === "ELIGIBLE";
}

function isProductIneligible(
  productName: string,
  state: CoreReduxState = getStore().getState()
) {
  const entry = getProductEligibility(productName, state);
  if (!isRequestSuccess(entry)) {
    return false;
  }

  const eligibilityState = entry.data?.purchaseEligibility?.eligibilityState;
  return eligibilityState === "INELIGIBLE";
}

export function isProductIneligibleOnlyDueToLocation(
  productName: string,
  state: CoreReduxState = getStore().getState()
) {
  const entry = getProductEligibility(productName, state);
  if (!isRequestSuccess(entry)) {
    return false;
  }

  const purchaseEligibility = entry.data?.purchaseEligibility;
  return isIneligibleOnlyDueToLocation(
    purchaseEligibility?.eligibilityState,
    purchaseEligibility?.ineligibleDetails
  );
}

/** INELIGIBLE for a reason a different shipping address cannot fix. */
export function isProductIneligibleForNonLocationReason(
  productName: string,
  state: CoreReduxState = getStore().getState()
) {
  return (
    isProductIneligible(productName, state) &&
    !isProductIneligibleOnlyDueToLocation(productName, state)
  );
}

// Prefix match, not an allowlist: a future vendor segment must fail closed.
const CLINICAL_VENDOR_SEGMENT_PREFIX_RE = /^eligibility_clinical_vendor_/;

export function isClinicalVendorSegment(detail: { ectSegmentId?: string }) {
  return (
    !!detail.ectSegmentId &&
    CLINICAL_VENDOR_SEGMENT_PREFIX_RE.test(detail.ectSegmentId)
  );
}

/**
 * Unknown counts as a conflict: this returns true only on a resolved verdict
 * with no vendor segment in ineligibleDetails or maybeEligibleDetails. An
 * unresolved/failed request and an ECT evaluation error (vendor segment in
 * maybeEligibleDetails) both fail closed — do not "fix" either back toward
 * fail-open; for a clinical safety gate the safe side is the default.
 */
export function hasNoClinicalVendorConflict(
  productName: string,
  state: CoreReduxState = getStore().getState()
) {
  const entry = getProductEligibility(productName, state);
  if (!isRequestSuccess(entry)) {
    return false;
  }

  const purchaseEligibility = entry.data?.purchaseEligibility;

  // Aggregate INELIGIBLE from other segments (BMI, medicare, etc.) does not
  // block here — medEligibility evaluates those from this session's answers.
  const hasVendorConflict = (purchaseEligibility?.ineligibleDetails ?? []).some(
    isClinicalVendorSegment
  );
  const vendorSegmentUnresolved = (
    purchaseEligibility?.maybeEligibleDetails ?? []
  ).some(isClinicalVendorSegment);

  return !hasVendorConflict && !vendorSegmentUnresolved;
}
